import { useEffect, useRef } from 'react';

type Listeners = Record<string, EventListener>;

/**
 * Registra os listeners de arrasto no window e garante que exatamente os mesmos
 * callbacks sejam removidos depois, inclusive no unmount.
 *
 * O código original da lib fazia `window.removeEventListener('mousemove', onDrag)`
 * com o `onDrag` de um render diferente do que havia sido registrado (cada render
 * cria uma closure nova). Resultado: desmontar o picker no meio do arrasto deixava
 * o listener vivo, e o mousemove seguinte estourava em
 * `node.current.getBoundingClientRect()` com o ref já nulo.
 */
export const useDragListeners = () => {
  const active = useRef<[string, EventListener][]>([]);

  const stop = () => {
    active.current.forEach(([type, fn]) => window.removeEventListener(type, fn));
    active.current = [];
  };

  const start = (listeners: Listeners, options?: AddEventListenerOptions) => {
    stop();

    Object.entries(listeners).forEach(([type, fn]) => {
      window.addEventListener(type, fn, options);
      active.current.push([type, fn]);
    });
  };

  useEffect(() => stop, []);

  return { start, stop };
};

export default useDragListeners;
